import { fetchAuthSession } from 'aws-amplify/auth';

let cachedToken: string | null = null;
let tokenExpiresAt = 0;
let pendingRefresh: Promise<string | null> | null = null;

const TOKEN_REFRESH_BUFFER_MS = 60_000;
const MAX_CACHE_DURATION_MS = 55 * 60_000; // 55 min hard cap regardless of exp

/**
 * Clears the cached auth token. Call on logout or session invalidation.
 */
export const clearCachedToken = (): void => {
  cachedToken = null;
  tokenExpiresAt = 0;
  pendingRefresh = null;
};

const refreshToken = async (): Promise<string | null> => {
  const session = await fetchAuthSession();
  const idToken = session?.tokens?.idToken;
  const token = idToken?.toString() || null;
  const expFromToken = ((idToken?.payload?.exp as number) || 0) * 1000;
  const maxExpiry = Date.now() + MAX_CACHE_DURATION_MS;

  cachedToken = token;
  tokenExpiresAt = Math.min(expFromToken, maxExpiry);

  return cachedToken;
};

/**
 * Retrieves authentication token if required.
 * Caches the token in memory and only calls fetchAuthSession()
 * when the token is expired or about to expire.
 * Deduplicates concurrent refresh calls.
 */
export const getAuthToken = async (
  requiresAuth: boolean,
): Promise<string | null> => {
  if (!requiresAuth) {
    return null;
  }

  if (cachedToken && Date.now() < tokenExpiresAt - TOKEN_REFRESH_BUFFER_MS) {
    return cachedToken;
  }

  // Deduplicate: if a refresh is already in flight, wait for it
  if (!pendingRefresh) {
    pendingRefresh = refreshToken().finally(() => {
      pendingRefresh = null;
    });
  }

  return pendingRefresh;
};

/**
 * Retrieves the Cognito access token (not ID token).
 * Used for operations that require the access token, e.g. TOTP verification.
 */
export const getAccessToken = async (): Promise<string | null> => {
  const session = await fetchAuthSession();
  return session?.tokens?.accessToken?.toString() || null;
};

/**
 * Builds request headers including authentication if required
 */
export const buildHeaders = async (
  requiresAuth: boolean,
  customToken?: string,
): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const authToken = await getAuthToken(requiresAuth);
  const token = customToken ?? authToken;

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};
